import { COLOR_CSS_VAR_NAME_CONFIG } from './css-var-name-config'

type SourceKind = 'own' | 'tenant'

type LightnessShadeFinalToSource<ExtraShadeName extends string> = {
  default: string
} & Record<ExtraShadeName, string>

type GroupData<ExtraShadeName extends string> = {
  sourceKind: SourceKind
  sourceColorGroupName: string
  lightnessShadeFinalToSource: LightnessShadeFinalToSource<ExtraShadeName>
}

type InvertableGroup<ExtraShadeName extends string> = {
  invertable: true
  straightData: GroupData<ExtraShadeName>
  invertedData: GroupData<ExtraShadeName>
}

type NonInvertableGroup<ExtraShadeName extends string> = {
  invertable: false
  data: GroupData<ExtraShadeName>
}

const createNonInvertableGroup = <ExtraShadeName extends string>(
  groupData: GroupData<ExtraShadeName>,
): NonInvertableGroup<string> => {
  return {
    invertable: false,
    data: groupData,
  }
}

const createInvertableGroup = <ExtraShadeName extends string>({
  straightData,
  invertedData,
}: {
  straightData: GroupData<ExtraShadeName>
  invertedData: GroupData<ExtraShadeName>
}): InvertableGroup<string> => {
  return {
    invertable: true,
    straightData,
    invertedData,
  }
}

const FINAL_COLOR = {
  'neutral': createNonInvertableGroup({
    sourceKind: 'own',
    sourceColorGroupName: 'neutral',
    lightnessShadeFinalToSource: {
      'default': 'default',
      'text-icons-secondary': 'text-icons-secondary',
      'text-icons-disabled': 'text-icons-disabled',
      'fill-text-dividers-stroke-disabled': 'fill-text-dividers-stroke-disabled',
      'fill-background-secondary': 'fill-background-secondary',
      'fill-surface-primary': 'fill-surface-primary',
    },
  }),
  'brand': (() => {
    const lightnessShadeFinalToSource = {
      'fill-pressed': 'fill-pressed',
      'fill-hover': 'fill-hover',
      'default': 'default',
      'stroke-primary': 'stroke-primary',
      'fill-primary': 'fill-primary',
    }
    return createInvertableGroup({
      straightData: {
        sourceKind: 'tenant',
        sourceColorGroupName: 'primary',
        lightnessShadeFinalToSource,
      },
      invertedData: {
        sourceKind: 'tenant',
        sourceColorGroupName: 'primary-inverted',
        lightnessShadeFinalToSource,
      },
    })
  })(),
  'primary-button-label': createInvertableGroup({
    straightData: {
      sourceKind: 'own',
      sourceColorGroupName: 'white',
      lightnessShadeFinalToSource: {
        default: 'default',
      },
    },
    invertedData: {
      sourceKind: 'tenant',
      sourceColorGroupName: 'primary-inverted-button-label',
      lightnessShadeFinalToSource: {
        default: 'default',
      },
    },
  }),
  'primary-text': createInvertableGroup({
    straightData: {
      sourceKind: 'tenant',
      sourceColorGroupName: 'primary-text',
      lightnessShadeFinalToSource: {
        default: 'default',
      },
    },
    invertedData: {
      sourceKind: 'tenant',
      sourceColorGroupName: 'primary-text-inverted',
      lightnessShadeFinalToSource: {
        default: 'default',
      },
    },
  }),
  'danger': createNonInvertableGroup({
    sourceKind: 'tenant',
    sourceColorGroupName: 'danger',
    lightnessShadeFinalToSource: {
      'default': 'default',
      'fill-stroke-secondary': 'fill-stroke-secondary',
    },
  }),
  'success': createNonInvertableGroup({
    sourceKind: 'tenant',
    sourceColorGroupName: 'success',
    lightnessShadeFinalToSource: {
      'default': 'default',
      'fill-stroke-secondary': 'fill-stroke-secondary',
    },
  }),
  'warning': createNonInvertableGroup({
    sourceKind: 'tenant',
    sourceColorGroupName: 'warning',
    lightnessShadeFinalToSource: {
      'default': 'default',
      'fill-stroke-secondary': 'fill-stroke-secondary',
    },
  }),
  'link': createNonInvertableGroup({
    sourceKind: 'tenant',
    sourceColorGroupName: 'link',
    lightnessShadeFinalToSource: {
      default: 'default',
    },
  }),
  'secondary': createNonInvertableGroup({
    sourceKind: 'tenant',
    sourceColorGroupName: 'secondary',
    lightnessShadeFinalToSource: {
      default: 'default',
    },
  }),
  'surface-background': createNonInvertableGroup({
    sourceKind: 'tenant',
    sourceColorGroupName: 'body-bg',
    lightnessShadeFinalToSource: {
      default: 'default',
    },
  }),
  'body-bg': createNonInvertableGroup({
    sourceKind: 'tenant',
    sourceColorGroupName: 'body-bg',
    lightnessShadeFinalToSource: {
      default: 'default',
    },
  }),
  'header-bg': createNonInvertableGroup({
    sourceKind: 'tenant',
    sourceColorGroupName: 'header-bg',
    lightnessShadeFinalToSource: {
      default: 'default',
    },
  }),
  'footer-bg': createNonInvertableGroup({
    sourceKind: 'tenant',
    sourceColorGroupName: 'footer-bg',
    lightnessShadeFinalToSource: {
      default: 'default',
    },
  }),
}

const constructFinalColorHslCoords = <ExtraShadeName extends string>({
  finalColorGroupName,
  finalColorGroupData: finalColorGroupData,
}: {
  finalColorGroupName: string
  finalColorGroupData: GroupData<ExtraShadeName>
}): React.CSSProperties => {
  const { sourceKind, sourceColorGroupName, lightnessShadeFinalToSource } = finalColorGroupData

  const finalVarNameBase = COLOR_CSS_VAR_NAME_CONFIG.final
  const sourceVarNameBase = COLOR_CSS_VAR_NAME_CONFIG[sourceKind]

  const hueDecl = {
    [`${finalVarNameBase}-h-${finalColorGroupName}`]: `var(${sourceVarNameBase}-h-${sourceColorGroupName})`,
  }

  const saturationDecl = {
    [`${finalVarNameBase}-s-${finalColorGroupName}`]: `var(${sourceVarNameBase}-s-${sourceColorGroupName})`,
  }

  const lightnessShadeFinalToSourceEntries = Object.entries(lightnessShadeFinalToSource)

  const lightnessDeclEntries = lightnessShadeFinalToSourceEntries.map(([finalShadeName, sourceShadeName]) => {
    const finalVarName = `${finalVarNameBase}-l-${finalColorGroupName}-${finalShadeName}`
    const finalVarValue = `var(${sourceVarNameBase}-l-${sourceColorGroupName}-${sourceShadeName})`

    return [finalVarName, finalVarValue]
  })

  return {
    ...hueDecl,
    ...saturationDecl,
    ...Object.fromEntries(lightnessDeclEntries),
  }
}

const constructComposedFinalColors = ({
  finalColorGroupName,
  finalColorShadeNames,
}: {
  finalColorGroupName: string
  finalColorShadeNames: string[]
}): React.CSSProperties => {
  const composedVarNameBase = COLOR_CSS_VAR_NAME_CONFIG.finalComposed
  const coordVarNameBase = COLOR_CSS_VAR_NAME_CONFIG.final

  return finalColorShadeNames.reduce((output, finalShadeName) => {
    const composedShadeNameSuffix = finalShadeName === 'default' ? '' : `-${finalShadeName}`

    const varName = `${composedVarNameBase}-${finalColorGroupName}${composedShadeNameSuffix}`
    const varValue = `hsl(var(${coordVarNameBase}-h-${finalColorGroupName}), var(${coordVarNameBase}-s-${finalColorGroupName}), var(${coordVarNameBase}-l-${finalColorGroupName}-${finalShadeName}))`

    return {
      ...output,
      [varName]: varValue,
    }
  }, {})
}

const getNonInvertableHslFinalColorCssStyles = <ExtraShadeName extends string>(): React.CSSProperties => {
  const nonInvertableFinalGroupEntries = Object.entries(FINAL_COLOR).filter(
    (groupEntry): groupEntry is [string, NonInvertableGroup<ExtraShadeName>] => !groupEntry[1].invertable,
  )

  const finalGroupHslStyleDecls = nonInvertableFinalGroupEntries.map(([finalColorGroupName, finalColorGroup]) => {
    return constructFinalColorHslCoords({
      finalColorGroupName,
      finalColorGroupData: finalColorGroup.data,
    })
  })

  return finalGroupHslStyleDecls.reduce((output, styleDecl) => {
    return {
      ...output,
      ...styleDecl,
    }
  })
}

export const getInvertableHslFinalColorCssStyles = <ExtraShadeName extends string>({
  shouldInvert,
}: {
  shouldInvert: boolean
}) => {
  const invertableFinalGroupEntries = Object.entries(FINAL_COLOR).filter(
    (groupEntry): groupEntry is [string, InvertableGroup<ExtraShadeName>] => groupEntry[1].invertable,
  )

  const finalGroupHslStyleDecls = invertableFinalGroupEntries.map(([finalColorGroupName, finalColorGroup]) => {
    return constructFinalColorHslCoords({
      finalColorGroupName,
      finalColorGroupData: shouldInvert ? finalColorGroup.invertedData : finalColorGroup.straightData,
    })
  })

  return finalGroupHslStyleDecls.reduce((output, styleDecl) => {
    return {
      ...output,
      ...styleDecl,
    }
  })
}

export const getComposedFinalColorCssStyles = (): React.CSSProperties => {
  const finalGroupEntries = Object.entries(FINAL_COLOR)

  const finalGroupComposedStyleDecls = finalGroupEntries.map(([finalColorGroupName, finalColorGroup]) => {
    const { lightnessShadeFinalToSource } = finalColorGroup.invertable ? finalColorGroup.invertedData : finalColorGroup.data

    return constructComposedFinalColors({
      finalColorGroupName,
      finalColorShadeNames: Object.keys(lightnessShadeFinalToSource),
    })
  })

  return finalGroupComposedStyleDecls.reduce((output, styleDecl) => {
    return {
      ...output,
      ...styleDecl,
    }
  })
}

export const finalColorInlineStyles = {
  getNonInvertableHsl: getNonInvertableHslFinalColorCssStyles,
  getInvertableHsl: getInvertableHslFinalColorCssStyles,
  getComposed: getComposedFinalColorCssStyles,
}
